import { CheckCircle2, LifeBuoy, LogOut, Menu, PanelLeftClose, PanelLeftOpen, X, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { cn } from "./cn";
import { Logo } from "./logo";
import { ThemeToggle } from "./theme";

export interface NavItem {
  label: string;
  to: string;
  icon?: LucideIcon;
}
export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export type LinkLike = ComponentType<{ to: string; className?: string; children: ReactNode; onClick?: () => void; "aria-current"?: "page" }>;

const isActive = (path: string, to: string) => (to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`));

/** Compact "need help?" card shown above the account block, only when a support URL is configured. */
function SupportCard({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="mb-2 block rounded-xl border border-accent-soft bg-accent-soft/60 p-3 transition-colors hover:border-accent/30"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <LifeBuoy size={15} />
        </span>
        <p className="text-[12.5px] font-semibold text-fg">Need help?</p>
      </div>
      <p className="mt-1.5 text-[11.5px] leading-snug text-muted">Get support from our team.</p>
      <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-accent">
        Contact Support
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <path d="M7 17 17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </a>
  );
}

const COLLAPSE_KEY = "hostdock.sidebar.collapsed";
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell({
  product,
  subtitle,
  nav,
  currentPath,
  Link,
  user,
  onLogout,
  supportHref,
  topbar,
  cta,
  children,
}: {
  product: string;
  /** Small label under the HostDock wordmark; defaults to the product name. */
  subtitle?: string;
  nav: NavGroup[];
  currentPath: string;
  Link: LinkLike;
  user: { name: string; email: string; subtitle?: string };
  onLogout(): void;
  /** When set, a "Need help?" support card links here. Omitted → no card (no dead links). */
  supportHref?: string;
  topbar?: ReactNode;
  /** Optional primary call-to-action shown under the brand (e.g. a "New website" link). Receives the collapsed state. */
  cta?: (collapsed: boolean) => ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  useEffect(() => setOpen(false), [currentPath]);
  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    try {
      localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
    } catch {
      // storage unavailable; the choice lasts for this page only
    }
  };

  const sidebar = (rail: boolean) => (
    <div className="flex h-full flex-col">
      <div className={cn("flex h-16 items-center gap-2.5", rail ? "justify-center px-2" : "px-5")}>
        <Logo size={30} />
        {!rail && (
          <div className="leading-tight">
            <p className="text-[15.5px] font-bold tracking-tight">HostDock</p>
            <p className="text-[11px] font-medium tracking-wide text-subtle">{subtitle ?? product}</p>
          </div>
        )}
      </div>
      {cta && <div className={cn("pb-2", rail ? "px-2.5" : "px-4")}>{cta(rail)}</div>}
      <nav className="hd-scroll flex-1 overflow-y-auto px-3 py-2" aria-label="Main">
        {nav.map((group, gi) => (
          <div key={group.label ?? gi} className="mb-3">
            {group.label &&
              (rail ? (
                <div className="mx-auto mb-2 h-px w-6 bg-line" aria-hidden />
              ) : (
                <p className="px-3 pt-1 pb-1.5 text-[10.5px] font-semibold tracking-[0.08em] text-subtle uppercase">{group.label}</p>
              ))}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const active = isActive(currentPath, item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to} className="relative">
                    {active && <span className="hd-gradient absolute top-1.5 bottom-1.5 -left-3 w-[3px] rounded-r-full" aria-hidden />}
                    <Link
                      to={item.to}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "group flex items-center gap-3 rounded-xl text-[13.5px] transition-colors",
                        rail ? "justify-center px-0 py-2" : "px-3 py-2",
                        active ? "bg-accent-soft font-semibold text-accent" : "text-muted hover:bg-surface-2 hover:text-fg",
                      )}
                    >
                      {Icon && (
                        <span title={rail ? item.label : undefined} className={cn("flex h-6 w-6 shrink-0 items-center justify-center transition-colors", active ? "text-accent" : "text-subtle group-hover:text-fg")}>
                          <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
                        </span>
                      )}
                      <span className={rail ? "sr-only" : "truncate"}>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className={cn("border-t border-line", rail ? "p-2" : "p-3")}>
        {supportHref && !rail && <SupportCard href={supportHref} />}
        <div className={cn("flex items-center gap-2 rounded-xl", rail ? "flex-col py-1" : "bg-surface-2/70 px-2 py-2")}>
          <div className="hd-gradient flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white shadow-accent" title={rail ? `${user.name} · ${user.subtitle ?? user.email}` : undefined}>
            {user.name.trim().charAt(0).toUpperCase() || "?"}
          </div>
          {!rail && (
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[13px] font-semibold">{user.name}</p>
              <p className="truncate text-[11.5px] text-subtle">{user.subtitle ?? user.email}</p>
            </div>
          )}
          <ThemeToggle />
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-surface-3 hover:text-fg"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      <aside className={cn("hidden shrink-0 border-r border-line bg-sidebar transition-[width] duration-200 lg:block", collapsed ? "w-[76px]" : "w-[268px]")}>{sidebar(collapsed)}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-[#0b0820]/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="hd-enter absolute inset-y-0 left-0 w-[280px] border-r border-line bg-sidebar shadow-lg">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-5 right-3 rounded-lg p-1 text-muted hover:bg-surface-2" aria-label="Close menu">
              <X size={16} />
            </button>
            {sidebar(false)}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface/75 px-4 backdrop-blur-xl md:px-6">
          <button type="button" onClick={() => setOpen(true)} className="rounded-lg p-1.5 text-muted hover:bg-surface-2 lg:hidden" aria-label="Open menu">
            <Menu size={18} />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden rounded-lg p-1.5 text-muted hover:bg-surface-2 hover:text-fg lg:inline-flex"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <div className="min-w-0 flex-1">{topbar}</div>
        </header>
        <main className="hd-scroll flex-1 overflow-y-auto">
          <div key={currentPath} className="hd-enter mx-auto w-full max-w-[1360px] px-4 py-6 md:px-6 md:py-8 lg:px-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

const AUTH_POINTS = [
  "Websites, domains, email and databases in one place",
  "Free automatic HTTPS for every connected domain",
  "Isolated containers for PHP, Node.js and Python apps",
  "Two-factor sign-in and full account activity history",
];

/** Split layout for sign-in and forced security steps: brand panel on large screens, centered card everywhere. */
export function AuthLayout({ product, children, footer }: { product: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-full">
      <aside className="hd-hero hidden w-[44%] max-w-[620px] flex-col justify-between p-10 lg:flex xl:p-14">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
              <rect x="5.5" y="5" width="3" height="9" rx="1" fill="#fff" />
              <rect x="10.5" y="5" width="3" height="9" rx="1" fill="#fff" opacity="0.85" />
              <rect x="15.5" y="5" width="3" height="9" rx="1" fill="#fff" opacity="0.7" />
              <rect x="4.5" y="16" width="15" height="2.4" rx="1.2" fill="#fff" />
            </svg>
          </span>
          <div className="leading-tight">
            <p className="text-[16px] font-bold tracking-tight">HostDock</p>
            <p className="text-[11px] font-medium tracking-wider text-white/70 uppercase">{product}</p>
          </div>
        </div>
        <div>
          <h2 className="max-w-md text-[34px] leading-[1.15] font-semibold tracking-tight">Hosting that stays out of your way.</h2>
          <p className="mt-3 max-w-md text-[15px] text-white/80">Everything you need to launch and run your websites, managed from one fast, modern control panel.</p>
          <ul className="mt-8 flex flex-col gap-3">
            {AUTH_POINTS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-[14px] text-white/90">
                <CheckCircle2 size={17} className="shrink-0 text-white" />
                {p}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[12px] text-white/60">© HostDock</p>
      </aside>
      <div className="flex min-h-full flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-7 flex items-center gap-2.5 lg:hidden">
          <Logo size={30} />
          <div className="leading-tight">
            <p className="text-[16px] font-bold tracking-tight">HostDock</p>
            <p className="text-[11px] font-medium tracking-wider text-subtle uppercase">{product}</p>
          </div>
        </div>
        <div className="hd-enter w-full max-w-[400px] rounded-2xl border border-line bg-surface p-7 shadow-md">{children}</div>
        <div className="mt-6 flex items-center gap-3 text-[12px] text-subtle">
          {footer}
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
